import type { ReactNode } from 'react';
import { getAttributeFromClause } from '@gonfalon/clauses/getAttributeFromClause';
import { isSegmentOp } from '@gonfalon/clauses/isSegmentOp';
import { translateToText } from '@gonfalon/clauses/translateToText';
import type { Clause as OpenApiClause } from '@gonfalon/clauses/types';
import { AttributeReference } from '@gonfalon/contexts/attributes/AttributeReference';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { isImmutable, List } from '@gonfalon/immutable-shim/immutableShim';
import cx from 'clsx';

import { useSegments } from '#hooks/useSegments';
import type { Clause as ImmutableClause } from '#utils/clause/records/Clause';
import { translateImmutableClauseToText } from '#utils/clause/translateImmutableClauseToText';
import type { Segment } from '#utils/segment/records/Segment';
import { mobileAttributesMapping } from '#utils/userAttributeUtils';

import { RuleDescriptionKind } from './ruleDescriptionKind';

import styles from './styles.module.css';

const ClauseInstruction = ({
  className,
  extraText,
  children,
}: {
  extraText?: string;
  className?: string;
  children: ReactNode;
}) => (
  <>
    <span className={cx(styles.ClauseInstructionDescription, className)}>{children}</span> {extraText}
  </>
);

type Clause = ImmutableClause | OpenApiClause;

const isImmutableClause = (clause: Clause): clause is ImmutableClause => isImmutable(clause);

const getSegmentOp = (clause: Clause) => (isImmutableClause(clause) ? clause.hasSegmentOp() : isSegmentOp(clause.op));

const translateClauseToText = (clause: Clause, segments: List<Segment>) => {
  if (isImmutableClause(clause)) {
    return translateImmutableClauseToText(clause, segments);
  }
  const attribute = new AttributeReference(getAttributeFromClause(clause)).toString();

  return translateToText(clause, attribute, mobileAttributesMapping, segments.toJS());
};

const SegmentClauseInstructionDescription = ({
  projKey,
  envKey,
  clause,
  extraText,
  className,
}: {
  projKey: string;
  envKey: string;
  clause: Clause | OpenApiClause;
  extraText?: string;
  className?: string;
}) => {
  const { segments } = useSegments({ projKey, envKey, enabled: getSegmentOp(clause) });

  const clauseText = translateClauseToText(clause, segments);

  return (
    <ClauseInstruction extraText={extraText} className={className}>
      {clauseText}
    </ClauseInstruction>
  );
};

export const ClauseInstructionDescription = ({
  projKey,
  envKey,
  clause,
  extraText,
  className,
  kind,
}: {
  projKey: string;
  envKey: string;
  clause: Clause;
  extraText?: string;
  className?: string;
  kind?: RuleDescriptionKind;
}) => {
  const extraClasses = cx({
    [styles.reorderedRule]: kind !== undefined && kind === RuleDescriptionKind.REORDERED_RULE,
  });

  const clauseText = translateClauseToText(clause, List());

  // special-case segment clauses because fetching and processing the list of segments is expensive and we don't want to
  // do it unless we really need to
  if (getSegmentOp(clause)) {
    return (
      <ErrorBoundary severity="medium" ignoredStatuses={[404]} fallbackRender={() => <>{clauseText}</>}>
        <SegmentClauseInstructionDescription
          className={cx(className, extraClasses)}
          clause={clause}
          extraText={extraText}
          projKey={projKey}
          envKey={envKey}
        />
      </ErrorBoundary>
    );
  }

  return (
    <ClauseInstruction className={extraClasses} extraText={extraText}>
      {clauseText}
    </ClauseInstruction>
  );
};
